import React, { useState, useContext, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import axios from "axios";
import SoftBox from "components/SoftBox";
import SoftTypography from "components/SoftTypography";
import SoftButton from "components/SoftButton";
import SoftInput from "components/SoftInput";
import Card from "@mui/material/Card";
import { AuthContext } from "context";
import { Box, Grid } from "@mui/material";
import { API_BASE_URL } from "services/config";
import { toPublicAssetUrl } from "services/assets";

const Login = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { login, activePortalSettings } = useContext(AuthContext);
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const portalName = activePortalSettings?.brand_name || "UDS Kundenportal";
  const portalColor = activePortalSettings?.primary_color || "#800000";
  const portalLogo = toPublicAssetUrl(activePortalSettings?.logo_url);

  useEffect(() => {
    // Beim Öffnen der Login-Seite ggf. alte Session serverseitig beenden
    axios.post(`${API_BASE_URL}/webusers/logout`, undefined, { withCredentials: true }).catch(() => {});
  }, []);

  useEffect(() => {
    if (error) setError(null);
  }, [username, password]);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (params.get("sso") !== "failed") return;

    const code = params.get("code");
    const messageByCode = {
      missing_token: "Auto-Login fehlgeschlagen: Login-Token fehlt.",
      sso_not_configured: "Auto-Login ist aktuell nicht verfuegbar.",
      missing_jti: "Auto-Login fehlgeschlagen: Ungueltiger Sicherheits-Token.",
      replay_blocked: "Auto-Login fehlgeschlagen: Token wurde bereits verwendet.",
      invalid_subject: "Auto-Login fehlgeschlagen: Benutzerkennung ungueltig.",
      user_not_found: "Auto-Login fehlgeschlagen: Benutzer wurde nicht gefunden.",
      user_disabled: "Auto-Login fehlgeschlagen: Benutzer ist deaktiviert.",
      token_expired: "Auto-Login fehlgeschlagen: Token ist abgelaufen.",
      token_not_active: "Auto-Login fehlgeschlagen: Token ist noch nicht gueltig.",
      token_invalid_signature: "Auto-Login fehlgeschlagen: Signatur ungueltig (Shared Secret pruefen).",
      token_invalid_audience: "Auto-Login fehlgeschlagen: Audience passt nicht (erwartet: uds-portal).",
      token_invalid_issuer: "Auto-Login fehlgeschlagen: Issuer passt nicht (erwartet: uds-products).",
      token_invalid_algorithm: "Auto-Login fehlgeschlagen: Algorithmus passt nicht (erwartet: HS256).",
      token_malformed: "Auto-Login fehlgeschlagen: Token-Format ist ungueltig.",
      token_invalid: "Auto-Login fehlgeschlagen: Token ist ungueltig.",
    };

    setError(messageByCode[code] || "Auto-Login fehlgeschlagen. Bitte melden Sie sich manuell an.");
  }, [location.search]);

  const validateInputs = () => {
    if (!username.trim()) {
      setError("Benutzername wird benötigt");
      return false;
    }
    if (!password.trim()) {
      setError("Passwort wird benötigt");
      return false;
    }
    return true;
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    
    if (!validateInputs()) return;

    setIsLoading(true);
    setError(null);

    try {
      await axios.post(`${API_BASE_URL}/webusers/login`, {
        username: username.trim(),
        password
      }, { withCredentials: true });

      // Cookie wurde serverseitig gesetzt – Benutzerdaten laden
      await login();
      
      navigate("/dashboard", { replace: true });
    } catch (err) {
      
      let errorMessage = "Ein Fehler ist aufgetreten";
      
      if (err.response) {
        switch (err.response.status) {
          case 401:
            errorMessage = "Benutzername oder Passwort ist falsch";
            break;
          case 404:
            errorMessage = "Benutzer nicht gefunden";
            break;
          case 403:
            errorMessage = "Benutzer ist deaktiviert";
            break;
          default:
            errorMessage = err.response.data?.message || err.response.data?.error || errorMessage;
        }
      } else if (err.request) {
        errorMessage = "Server nicht erreichbar. Bitte überprüfen Sie Ihre Internetverbindung.";
      } else {
        errorMessage = err.message;
      }
      
      setError(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Box
      display="flex"
      justifyContent="center"
      alignItems="center"
      minHeight="100vh"
      width="100vw"
      position="fixed"
      top={0}
      left={0}
      zIndex={9999}
      sx={{
        background: "linear-gradient(135deg, #f5f7fa 0%, #e4e8eb 100%)",
        position: 'relative',
        '&::before': {
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundImage: 'radial-gradient(circle at 25px 25px, #333 2%, transparent 0%), radial-gradient(circle at 75px 75px, #333 2%, transparent 0%)',
          backgroundSize: '100px 100px',
          opacity: 0.03,
        }
      }}
    >
      <Card
        sx={{
          display: 'flex',
          maxWidth: '1200px',
          width: '90%',
          height: '80vh',
          maxHeight: '700px',
          margin: '20px',
          borderRadius: '20px',
          overflow: 'hidden',
          boxShadow: '0 20px 60px rgba(0,0,0,0.08)',
          background: 'rgba(255, 255, 255, 0.95)',
          backdropFilter: 'blur(10px)',
        }}
      >
        <Grid container sx={{ height: '100%' }}>
          {/* Login Form Section */}
          <Grid item xs={12} md={6} sx={{ height: '100%' }}>
            <SoftBox
              display="flex"
              flexDirection="column"
              justifyContent="center"
              padding={{ xs: 2, sm: 4 }}
              height="100%"
              sx={{
                maxWidth: '450px',
                margin: '0 auto',
                width: '100%'
              }}
            >
              {portalLogo && (
                <SoftBox mb={2}>
                  <Box
                    component="img"
                    src={portalLogo}
                    alt={portalName}
                    onError={(event) => {
                      event.currentTarget.style.display = "none";
                    }}
                    sx={{ maxWidth: 180, maxHeight: 64, objectFit: "contain" }}
                  />
                </SoftBox>
              )}
              <SoftTypography 
                variant="h3" 
                fontWeight="bold" 
                mb={1}
                sx={{ 
                  color: '#2C3E50',
                  fontSize: { xs: '2rem', md: '2.5rem' },
                  textShadow: '0 2px 4px rgba(0,0,0,0.05)'
                }}
              >
                {portalName}
              </SoftTypography>
              <SoftTypography 
                variant="body2" 
                mb={4}
                sx={{ color: '#cccccc' }}
              >
                Bitte melden Sie sich an, um fortzufahren
              </SoftTypography>

              <form onSubmit={handleLogin}>
                <SoftBox mb={3}>
                  <SoftTypography variant="body2" fontWeight="medium" mb={1}>
                    Benutzername
                  </SoftTypography>
                  <SoftInput
                    type="text"
                    value={username}
                    onChange={(e) => setUsername(e.target.value)}
                    placeholder="Ihr Benutzername"
                    fullWidth
                    disabled={isLoading}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        borderRadius: '10px',
                      }
                    }}
                  />
                </SoftBox>

                <SoftBox mb={3}>
                  <SoftTypography variant="body2" fontWeight="medium" mb={1}>
                    Passwort
                  </SoftTypography>
                  <SoftInput
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="Ihr Passwort"
                    fullWidth
                    disabled={isLoading}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        borderRadius: '10px',
                      }
                    }}
                  />
                </SoftBox>

                <SoftBox mb={3}>
                  <SoftTypography 
                    variant="button"
                    color="info"
                    sx={{ 
                      cursor: 'pointer', 
                      float: 'right',
                      '&:hover': { 
                        textDecoration: 'underline',
                        opacity: 0.8 
                      } 
                    }}
                  >
                    Passwort vergessen?
                  </SoftTypography>
                </SoftBox>

                {error && (
                  <SoftTypography 
                    variant="caption" 
                    color="error" 
                    display="block" 
                    mb={2}
                    sx={{
                      padding: '8px',
                      borderRadius: '8px',
                      backgroundColor: 'rgba(244, 67, 54, 0.1)',
                      textAlign: 'center'
                    }}
                  >
                    {error === "Username is required" ? "Benutzername ist erforderlich" :
                     error === "Password is required" ? "Passwort ist erforderlich" :
                     error === "Invalid response format" ? "Ungültiges Anmeldeformat" :
                     error}
                  </SoftTypography>
                )}

                <SoftButton 
                  type="submit" 
                  variant="gradient" 
                  color="info" 
                  fullWidth
                  disabled={isLoading}
                  sx={{
                    borderRadius: '10px',
                    padding: '12px',
                    transition: 'all 0.3s',
                    background: `linear-gradient(135deg, ${portalColor} 0%, #1f2937 100%)`,
                    '&:hover': {
                      transform: 'translateY(-2px)',
                      boxShadow: '0 7px 14px rgba(0,0,0,0.3)',
                      filter: 'brightness(110%)'
                    }
                  }}
                >
                  {isLoading ? (
                    <Box
                      component="span"
                      sx={{
                        display: 'inline-block',
                        animation: 'spin 1s linear infinite',
                        '@keyframes spin': {
                          '0%': { transform: 'rotate(0deg)' },
                          '100%': { transform: 'rotate(360deg)' }
                        }
                      }}
                    >
                      ⭮
                    </Box>
                  ) : "Anmelden"}
                </SoftButton>
              </form>
            </SoftBox>
          </Grid>

          {/* Illustration Section */}
          <Grid item xs={12} md={6} sx={{ height: '100%' }}>
            <Box
              sx={{
                height: '100%',
                background: `linear-gradient(180deg, ${portalColor} 0%, #111827 100%)`,
                position: 'relative',
                overflow: 'hidden',
                '&::before': {
                  content: '""',
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: '-50%',
                  background: `linear-gradient(0deg, rgba(17,24,39,0.95) 0%, ${portalColor}ee 100%)`,
                  transform: 'skewY(-12deg)',
                  transformOrigin: 'top left',
                }
              }}
            >
              <div
                style={{
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'center',
                  alignItems: 'center',
                  padding: '2rem',
                }}
              >
                <svg
                  width="300"
                  height="300"
                  viewBox="0 0 100 100"
                  style={{
                    filter: 'drop-shadow(0 4px 8px rgba(0,0,0,0.2))'
                  }}
                >
                  <circle cx="50" cy="50" r="45" fill="#fff" opacity="0.2" />
                  <path
                    d="M50 25C36.2 25 25 36.2 25 50s11.2 25 25 25 25-11.2 25-25S63.8 25 50 25zm0 45c-11 0-20-9-20-20s9-20 20-20 20 9 20 20-9 20-20 20z"
                    fill="#fff"
                  />
                  <circle cx="50" cy="45" r="8" fill="#fff" />
                  <path
                    d="M50 55c-8.3 0-15 3.7-15 8.3v1.7h30v-1.7C65 58.7 58.3 55 50 55z"
                    fill="#fff"
                  />
                </svg>

                <SoftTypography
                  variant="h4"
                  color="white"
                  textAlign="center"
                  mt={4}
                  sx={{ 
                    zIndex: 1,
                    textShadow: '0 2px 4px rgba(0,0,0,0.2)',
                    letterSpacing: '0.5px'
                  }}
                >
                  Willkommen im {portalName}
                </SoftTypography>
                <SoftTypography
                  variant="body2"
                  color="white"
                  textAlign="center"
                  mt={2}
                  sx={{ 
                    opacity: 0.9,
                    zIndex: 1,
                    maxWidth: '80%',
                    lineHeight: 1.6
                  }}
                >
                  Greifen Sie auf Ihren persönlichen Arbeitsbereich zu und verwalten Sie Ihre Projekte effizient
                </SoftTypography>
              </div>

              {/* Neue animierte Linien */}
              <Box
                sx={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  overflow: 'hidden',
                  zIndex: 0,
                }}
              >
                {/* Primäre Linien */}
                {[...Array(15)].map((_, i) => (
                  <div
                    key={`primary-${i}`}
                    style={{
                      position: 'absolute',
                      background: 'linear-gradient(90deg, rgba(255,255,255,0) 0%, rgba(255,255,255,0.3) 50%, rgba(255,255,255,0) 100%)',
                      transform: `rotate(${35 + (i * 2)}deg) translateX(${-50 + (i * 20)}%)`,
                      width: '200%',
                      height: '2px',
                      top: `${(i * 8)}%`,
                      animation: `slideRight${i} ${12 + i * 2}s linear infinite`,
                      boxShadow: '0 0 12px rgba(255,255,255,0.4)',
                      borderRadius: '2px',
                      opacity: 0.7,
                    }}
                  />
                ))}

                {/* Gegenläufige Linien */}
                {[...Array(10)].map((_, i) => (
                  <div
                    key={`counter-${i}`}
                    style={{
                      position: 'absolute',
                      background: 'linear-gradient(90deg, rgba(255,255,255,0) 0%, rgba(255,255,255,0.15) 50%, rgba(255,255,255,0) 100%)',
                      transform: `rotate(${-15 - (i * 2)}deg) translateX(${-30 + (i * 15)}%)`,
                      width: '200%',
                      height: '1px',
                      top: `${(i * 12)}%`,
                      animation: `slideLeft${i} ${15 + i * 3}s linear infinite`,
                      boxShadow: '0 0 8px rgba(255,255,255,0.2)',
                      borderRadius: '1px',
                      opacity: 0.5,
                    }}
                  />
                ))}

                {/* Pulsierende Punkte */}
                {[...Array(8)].map((_, i) => (
                  <div
                    key={`pulse-${i}`}
                    style={{
                      position: 'absolute',
                      width: '4px',
                      height: '4px',
                      borderRadius: '50%',
                      background: 'rgba(255,255,255,0.8)',
                      top: `${20 + (i * 10)}%`,
                      left: `${10 + (i * 15)}%`,
                      animation: `pulse${i} ${4 + i}s ease-in-out infinite`,
                      boxShadow: '0 0 15px rgba(255,255,255,0.5)',
                    }}
                  />
                ))}

                <style>
                  {[...Array(15)].map((_, i) => `
                    @keyframes slideRight${i} {
                      0% { transform: rotate(${35 + (i * 2)}deg) translateX(-150%); }
                      100% { transform: rotate(${35 + (i * 2)}deg) translateX(150%); }
                    }
                  `).join('')}
                  
                  {[...Array(10)].map((_, i) => `
                    @keyframes slideLeft${i} {
                      0% { transform: rotate(${-15 - (i * 2)}deg) translateX(150%); }
                      100% { transform: rotate(${-15 - (i * 2)}deg) translateX(-150%); }
                    }
                  `).join('')}
                  
                  {[...Array(8)].map((_, i) => `
                    @keyframes pulse${i} {
                      0%, 100% { transform: scale(1); opacity: 0.3; }
                      50% { transform: scale(2); opacity: 0.7; }
                    }
                  `).join('')}
                </style>
              </Box>
            </Box>
          </Grid>
        </Grid>
      </Card>
    </Box>
  );
};

export default Login;
